import { useSuspenseQuery } from '@tanstack/react-query'
import type { Channel as StreamChannelType } from 'stream-chat'

import { getMatchById } from 'src/shared/api/partner-match/use-match-by-id'
import { getReferralShareById } from 'src/shared/api/referrals/use-referral-share-by-id'
import { getConsumerById } from 'src/shared/features/consumers/api/use-consumer-by-id'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { getUserById } from 'src/shared/features/users/api/accounts/use-user-by-id'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import { formatInternationalPhoneNumber } from 'src/shared/utils/format/format-intl-phone-number'
import { formatPrintedReferralAddress } from 'src/shared/utils/format-field-value'

export type ChannelType = 'messaging' | 'network'
export type ChatType = 'referral' | 'network' | 'match' | 'sneak' | 'consumer' | 'dm'
export type ChatCategory = 'partner_match' | 'referral' | 'network' | 'sneak' | 'dm' | 'consumer_dm'

type ChatData = {
  data: any
  title: string
  description: string
  chatPartner: any | null
  type: ChatType
}

const getChatData = async (
  channelId: string | null,
  type: ChannelType,
  myId: number,
  otherMemberId: string | null, // otherMemberId can be string if it's a consumer DM channel
  category: ChatCategory,
): Promise<ChatData | null> => {
  try {
    if (!channelId) {
      return null
    }

    // Referral
    if (type === 'messaging') {
      if (category === 'consumer_dm') {
        // get consumer
        const consumerId = otherMemberId ? otherMemberId.toString().split('-')[1] : null

        if (!consumerId) {
          return {
            data: {},
            title: 'DM Chat',
            type: 'consumer',
            description: 'Talk to your DM',
            chatPartner: null,
          }
        }

        const consumer = await getConsumerById(consumerId)

        if (!consumer) {
          return {
            data: {},
            title: 'DM Chat',
            type: 'consumer',
            description: 'Talk to your DM',
            chatPartner: null,
          }
        }

        return {
          data: {},
          title: [consumer.firstName, consumer.lastName].filter(Boolean).join(' '),
          description:
            formatInternationalPhoneNumber(consumer.phoneNumber || '') ||
            consumer.primaryEmail ||
            'No phone number or email found',
          chatPartner: {
            ...consumer,
            phoneNumber: formatInternationalPhoneNumber(consumer.phoneNumber || ''),
          },
          type: 'consumer',
        }
      }
      if (category === 'dm') {
        const chatPartner = await getUserById(Number(otherMemberId))

        if (!chatPartner) {
          return {
            data: {},
            title: 'DM Chat',
            type: 'dm',
            description: 'Talk to your DM',
            chatPartner: null,
          }
        }

        return {
          data: {},
          title: chatPartner.fullName,
          description: professionMap[chatPartner?.profession as Profession]?.label,
          chatPartner,
          type: 'dm',
        }
      }
      const splitChannelId = channelId.toString().split('-')
      if (splitChannelId.length !== 3) return null

      const referralShareId = Number(splitChannelId[2])
      const referralShare = await getReferralShareById(referralShareId)
      if (!referralShare) {
        return {
          title: 'Referral Chat',
          type: 'referral',
          data: null,
          description: 'No info found',
          chatPartner: null,
        }
      }

      const isSender = referralShare.senderId === myId
      const partnerId = isSender ? referralShare.receiverId : referralShare.senderId
      const chatPartner = await getUserById(partnerId)

      if (!chatPartner) {
        return {
          data: referralShare,
          description: formatPrintedReferralAddress(referralShare, isSender || referralShare.status === 'approved'),
          title: 'Referral Chat',
          chatPartner: null,
          type: 'referral',
        }
      }

      return {
        data: referralShare,
        title: chatPartner.fullName,
        description: formatPrintedReferralAddress(referralShare, isSender || referralShare.status === 'approved'),
        chatPartner,
        type: 'referral',
      }
    }

    // Partner Match
    if (type === 'network' && category === 'partner_match') {
      const matchId = Number(channelId)
      const match = await getMatchById(matchId)

      if (!match) {
        return {
          title: 'Partner Match',
          type: 'match',
          data: null,
          description: 'No info found',
          chatPartner: null,
        }
      }

      const isCreatedByMe = match.createdByUserId === myId
      const chatPartnerId = isCreatedByMe ? match.matchedWithUserId : match.createdByUserId

      if (!chatPartnerId) {
        return {
          data: match,
          title: 'Partner Match',
          type: 'match',
          description: 'Talk to your match',
          chatPartner: null,
        }
      }

      const chatPartner = await getUserById(chatPartnerId)
      if (!chatPartner) {
        return {
          data: match,
          title: 'Partner Match',
          type: 'match',
          description: 'Talk to your match',
          chatPartner: null,
        }
      }

      return {
        data: match,
        title: chatPartner.fullName,
        description: professionMap[chatPartner?.profession as Profession]?.label,
        chatPartner,
        type: 'match',
      }
    }

    // Network
    if (type === 'network' && category === 'network') {
      const chatPartner = await getUserById(Number(otherMemberId))

      if (!chatPartner) {
        return {
          data: {},
          title: 'Network Chat',
          type: 'network',
          description: 'No info found',
          chatPartner: null,
        }
      }

      return {
        data: {},
        title: chatPartner.fullName,
        description: professionMap[chatPartner?.profession as Profession]?.label,
        chatPartner,
        type: 'network',
      }
    }

    // Sneak
    if (type === 'network' && category === 'sneak') {
      const chatPartner = await getUserById(Number(otherMemberId))

      if (!chatPartner) {
        return {
          data: {},
          title: 'Sneak Chat',
          type: 'sneak',
          description: 'No info found',
          chatPartner: null,
        }
      }

      return {
        data: {},
        title: chatPartner.fullName,
        description: professionMap[chatPartner?.profession as Profession]?.label,
        chatPartner,
        type: 'sneak',
      }
    }

    return null
  } catch (error) {
    console.error(error)
    return null
  }
}

export const useChatData = (channel: StreamChannelType | null) => {
  const { data: myUser } = useMyUserData()
  const myId = myUser?.id ?? -1
  const channelId = channel?.id || null
  const type = channel?.type as ChannelType
  const category = channel?.data?.category as ChatCategory

  const members = channel?.state?.members || []
  const memberIds = Object.keys(members)
  const otherMemberId = memberIds.find((id) => id !== myId.toString())

  return useSuspenseQuery({
    queryKey: ['chat-data', channelId, type, category, myId, otherMemberId ? otherMemberId : 'N/A'],
    queryFn: () => getChatData(channelId, type, myId, otherMemberId ? otherMemberId : null, category),
    retry: 3,
    staleTime: 5 * 60 * 1000,
  })
}
